import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";

import SubscriptionHeader from "../../components/Subscription/SubscriptionHeader";
import PendingPaymentAlert from "../../components/Subscription/PendingPaymentAlert";
import PlanGrid from "../../components/Subscription/PlanGrid";
import FreePlanDialog from "../../dialogs/SubscriptionDialogs/FreePlanDialog";
import PaymentMethodDialog from "../../dialogs/SubscriptionDialogs/PaymentMethodDialog";
import PsidDetailsDialog from "../../dialogs/SubscriptionDialogs/PsidDetailsDialog";

import useSubscriptionPlans from "../../utils/hooks/subscription/useSubscriptionPlans";
import usePendingPsid from "../../utils/hooks/subscription/usePendingPsid";
import useSubscriptionPayment from "../../utils/hooks/subscription/useSubscriptionPayment";

import PayzenPaymentModal from "../../dialogs/SubscriptionDialogs/CardPaymentDialog";

export default function SubscriptionPlans() {
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  // console.log("subscription plan", userInfo);

  const [selectedPlan, setSelectedPlan] = useState(null);

  const userId =
    userInfo?._id || userInfo?.id || userInfo?.user?._id || userInfo?.user?.id;

  const currentPlanId =
    userInfo?.currentPlanId ||
    userInfo?.planId ||
    userInfo?.user?.currentPlanId;

  const planExpiryDate =
    userInfo?.planExpiration ||
    userInfo?.expiryDate ||
    userInfo?.subscriptionExpiry ||
    userInfo?.user?.planExpiryDate ||
    userInfo?.user?.expiryDate;

  const {
    plans,
    loading: fetchingPlans,
    error: fetchError,
  } = useSubscriptionPlans({
    userId,
    currentPlanId,
  });

  const { pendingPsid, psidData, savePsid, clearPsid } = usePendingPsid({
    selectedPlan,
  });

  const {
    dialogs,
    loading,
    formToken,

    openPlan,

    closeFreePlanDialog,
    closePaymentMethodDialog,
    closePsidDialog,
    closeCardModal,

    openPsidDialog,

    confirmFreePlan,
    confirmPayment,

    handlePsidSuccess,
    handleCardSuccess,
    handleCardError,
  } = useSubscriptionPayment({
    userId,
    selectedPlan,
    setSelectedPlan,
    savePsid,
    clearPsid,
  });

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    openPlan(plan);
  };

  const handleViewPendingVoucher = () => {
    openPsidDialog();
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: { xs: 1, lg: 3 },
        height: "calc(100vh - 50px)",
        width: { xs: "100%", lg: "calc(100% - 45px)" },
        overflowY: "auto",
      }}
    >
      <SubscriptionHeader />

      {pendingPsid && !dialogs.psidDetails && (
        <PendingPaymentAlert
          psid={pendingPsid}
          onViewVoucher={handleViewPendingVoucher}
        />
      )}

      {fetchingPlans ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CircularProgress color='primary' size={40} />
        </Box>
      ) : fetchError ? (
        <Box
          sx={{
            maxWidth: "600px",
            mx: "auto",
            my: 4,
          }}
        >
          <Alert severity='error'>{fetchError}</Alert>
        </Box>
      ) : (
        <PlanGrid
          plans={plans}
          currentPlanId={currentPlanId}
          planExpiryDate={planExpiryDate}
          onSelectPlan={handleSelectPlan}
        />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          mt: { xs: "1rem", lg: "3rem" },
          borderRadius: "4px",
          padding: "8px 0",
        }}
      >
        <Typography
          textAlign='center'
          sx={{
            fontSize: { xs: "14px", lg: "16px" },
            fontStyle: "italic",
          }}
        >
          <Typography component='span' sx={{ fontWeight: "600" }}>
            Important Notice:
          </Typography>{" "}
          The subscription plans displayed reflect our future pricing structure.
          Current access during the beta phase is provided under temporary terms
          for evaluation purposes.
        </Typography>
      </Box>

      <FreePlanDialog
        open={dialogs.freePlan}
        onClose={closeFreePlanDialog}
        selectedPlan={selectedPlan}
        onConfirm={confirmFreePlan}
        loading={loading}
      />

      <PaymentMethodDialog
        open={dialogs.paymentMethod}
        onClose={closePaymentMethodDialog}
        selectedPlan={selectedPlan}
        onConfirmPayment={confirmPayment}
        loading={loading}
      />

      <PsidDetailsDialog
        open={dialogs.psidDetails}
        onClose={closePsidDialog}
        psidData={psidData}
        selectedPlan={selectedPlan}
        onSuccess={handlePsidSuccess}
      />

      <PayzenPaymentModal
        open={dialogs.card}
        onClose={closeCardModal}
        formToken={formToken}
        userId={userId}
        selectedPlan={selectedPlan}
        onSuccess={handleCardSuccess}
        onError={handleCardError}
      />
    </Container>
  );
}
